import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';

import { useMediaQuery } from '../../hooks/useMediaQuery';
import { Accordion } from '../accordion';
import { BannerVariant } from '../banner-variant';
import { Cards } from '../cards';
import { Greenhouse } from '../greenhouse';
import { Hero } from '../hero';
import { ImageGridWithColumns } from '../imageGridWithColumns';
import { ImageSlider } from '../imageSlider';
import { Iterable } from '../iterable';
import { LearnMore } from '../learnMore';
import { LockingScroll } from '../lockingScroll';
import { MarketoForm } from '../marketo-form';
import { Marquee } from '../marquee';
import { Pricing } from '../pricing';
import { Stacks } from '../stacks';
import * as styles from './tabbed-content.module.css';

export function TabbedContent({ tabs = [] }) {
	// hooks
	const isMobile = useMediaQuery('(max-width: 799px)');

	// state
	const [activeTab, setActiveTab] = useState(tabs[0]?._key); // State for active tab
	const [activeContent, setActiveContent] = useState(tabs[0]?.tabContent); // State for active tab content
	const [fadeInContent, setFadeInContent] = useState(false); // State to trigger content fade in
	const [fadeOutContent, setFadeOutContent] = useState(false); // State to trigger content fade out
	const [pinTabs, setPinTabs] = useState(false); // State for pinning tabs

	const tabbedContentRef = useRef(null);

	// scroll duration & ease control
	const scrollToTop = (duration) => {
		const tabbedContent = tabbedContentRef.current;
		if (tabbedContent) {
			const targetPosition =
				tabbedContent.getBoundingClientRect().top +
				window.pageYOffset -
				(isMobile ? 80 : 140); // Adjust offset as needed
			const startPosition = window.pageYOffset;
			const distance = targetPosition - startPosition;
			const startTime = performance.now();

			const animateScroll = (currentTime) => {
				const elapsedTime = currentTime - startTime;
				const progress = Math.min(elapsedTime / duration, 1);
				const easeInOutQuad = (t) =>
					t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
				window.scrollTo(
					0,
					startPosition + distance * easeInOutQuad(progress)
				);

				if (elapsedTime < duration) {
					requestAnimationFrame(animateScroll);
				}
			};

			requestAnimationFrame(animateScroll);
		}
	};

	const handleChangeTab = (tabKey) => {
		if (tabKey === activeTab) {
			return; // Do nothing if clicking on the active tab
		}

		// 1. Set active tab immediately and start fading out previous tab's content
		setActiveTab(tabKey);
		setFadeOutContent(true);

		// 2. Start scroll to top of tabbed content
		setTimeout(() => {
			scrollToTop(500); // Adjust duration
		}, 150); // Delay

		// 3. Delay updating active content until fade out completes
		setTimeout(() => {
			const newActiveTab = tabs.find((tab) => tab._key === tabKey);
			setActiveContent(newActiveTab?.tabContent || []);
			setFadeOutContent(false); // Reset fade-out state
		}, 700); // Delay

		// 3. Fade in new content
		setTimeout(() => {
			setFadeInContent(true); // Trigger fade-in after setting new content
		}, 900); // Delay
	};

	// add a scroll listener to the window
	useEffect(() => {
		const handleScroll = () => {
			const tabbedContent = document.querySelector('.hide');

			if (tabbedContent) {
				setPinTabs(true);
			} else {
				setPinTabs(false);
			}
		};

		window.addEventListener('scroll', handleScroll);

		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, []);

	return (
		<div
			className={classNames(
				styles.moduleContainer,
				'tabbed-content-module'
			)}
		>
			<div
				className={classNames(
					pinTabs && styles.pinTabsToTop,
					styles.tabsContainerWrapper
				)}
			>
				<nav className={styles.tabsContainer}>
					{tabs?.map((tab) => {
						const { _key, tabColor, tabTitle } = tab;

						// TODO: pull this into a global var somewhere
						const textColorMap = {
							cbge: 'cblgr',
							cw: 'cblgr',
							cblgr: 'cw',
							cgrn: 'cw',
							cmgr: 'cw',
							csgr: 'cblgr',
							cpgr: 'cblgr',
							cslm: 'cw',
							ctlm: 'cw',
							cdtl: 'cw',
							csre: 'cw',
							cdgr: 'cw',
							cgr: 'cw',
							cbb: 'cw',
							crst: 'cw',
							cgld: 'cw',
							cprp: 'cw',
						};

						return (
							<button
								className={classNames(
									activeTab === _key && `b${tabColor}`,
									styles.tabContainer
								)}
								id={_key}
								key={_key}
								onClick={() => handleChangeTab(_key)}
							>
								<p
									className={classNames(
										'body-display',
										activeTab === _key &&
											textColorMap[tabColor]
									)}
								>
									{tabTitle}
								</p>
							</button>
						);
					})}
				</nav>
			</div>
			<div ref={tabbedContentRef}>
				<div
					className={classNames(
						fadeOutContent && styles.fadeOut,
						fadeInContent && styles.fadeIn
					)}
				>
					{activeContent.map((content) => {
						const { _key, _type } = content;

						switch (_type) {
							case 'accordion':
								return <Accordion key={_key} {...content} />;
							case 'bannerVariant':
								return (
									<BannerVariant key={_key} {...content} />
								);
							case 'cards':
								return <Cards key={_key} {...content} />;
							case 'greenhouse':
								return <Greenhouse key={_key} {...content} />;
							case 'hero':
								return <Hero key={_key} {...content} />;
							case 'iterableContainer':
								return <Iterable key={_key} {...content} />;
							case 'imageGridWithColumns':
								return (
									<ImageGridWithColumns
										key={_key}
										{...content}
									/>
								);
							case 'lockingScrollModule':
								return (
									<LockingScroll key={_key} {...content} />
								);
							case 'learnMore':
								return <LearnMore key={_key} {...content} />;
							case 'marketoContainer':
								return <MarketoForm key={_key} {...content} />;
							case 'marqueeContainer':
								return <Marquee key={_key} {...content} />;
							case 'pricingContainer':
								return <Pricing key={_key} {...content} />;
							case 'slider':
								return <ImageSlider key={_key} {...content} />;
							case 'stacks':
								return <Stacks key={_key} {...content} />;
							default:
								return null;
						}
					})}
				</div>
			</div>
		</div>
	);
}
